import React, { useEffect, useState, useRef } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';

import useCheckoutState, { checkoutStates } from '../../hooks/useCheckoutState';
import useAiTarget from '../../hooks/useAiTarget';
import ProductPwywField from '../Product/ProductPwywField';
import APIErrorMessage from './components/APIErrorMessage';
import FormField from './components/FormField';
import FormLayoutFields from './components/FormLayout/FormLayoutFields';
import Button from '../../components/Button';
import DiscountCodeBox from './components/DiscountCodeBox';
import { GUEST_ERROR_MESSAGES, layoutHasDiscountCode } from '@repo/lib';
import Spinner from '../../components/Spinner';
import useCheckoutFormHelpers from '../../hooks/useCheckoutFormHelpers';
import useCheckoutValidation from '../../hooks/useCheckoutValidation';
import useCheckoutHelpers from '../../hooks/useCheckoutHelpers';
import { useCheckoutLocalization } from '../../hooks/useCheckoutLocalization';
import PaymentMethods, { getPaymentFormType } from './components/PaymentMethods';
import getPayButtonText from './lib/getPayButtonText';
import { isRecurringPurchase } from '../../lib/multiPrice';
import clsx from 'clsx';
import ExpressCheckoutElement from './ExpressCheckoutElement';
import TestCardPayButton, { shouldShowTestCardButton } from './TestCardPayButton';
import useRecaptcha from '../../hooks/useRecaptcha';
import useCheckoutUpsell from '../../hooks/useCheckoutUpsell';
import useCartCost from '../../hooks/useCartCost';
import PriceBreakdown from '../PriceBreakdown';
import CoverProcessingFees from './components/CoverProcessingFees';
import { PAGE_TYPES } from '../../enums/page-types';
import checkShouldShowPaymentMethods from '../../lib/checkShouldShowPaymentMethods';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import PaymentOptions from './PaymentOptions';
import useCheckoutCoupon from '@repo/checkout/hooks/useCheckoutCoupon';
import StyledElement from '../StyledElement';
import getThemeLabels from '../../lib/getThemeLabels';
// import AddressField from './components/AddressField';
import useSubmitDisabled from '../../hooks/useSubmitDisabled';
import checkPaymentDetails from '../../lib/checkPaymentDetails';
import useSelectedPrice from '../../hooks/useSelectedPrice';
import { validateAddressValue, formatAddressValidationError } from '../../lib/validateAddressValue';
import useFieldVisibility from './components/FormField/useFieldVisibility';
import { useMemo } from 'react';
import logException from '@repo/checkout/services/logException';
import GuestRows from '../GuestRows';
import reportValidityOutsideGuests from '../../lib/guests/reportValidityOutsideGuests';
import useGuestsBeforePay from '../../lib/guests/useGuestsBeforePay';
import { currentGuestRows } from '../../lib/guests/checkoutGuests';
import ErrorMessage from './components/ErrorMessage';

const CheckoutForm = () => {
  const {
    formError,
    setFormError,
    state,
    form,
    product,
    checkout,
    page,
    payWhatYouWantPrice,
    setState,
    seller,
    formRef,
    query,
    isScreenshot,
    isPreview,
    isTestingPayments,
    isTestMode,
    isUpsell,
    isUpsellRetry,
    showCouponCodeField,
    setShowCouponCodeField,
    paymentOption,
    isMeasurer,
    theme,
    currencyLocale,
    hasFocusedCheckoutRef,
    hasInitiatedPaymentRef,
    draftCheckout,
    updateDraftChargeDebounced,
    cancelUpdateDraftCharge,
  } = useCheckoutState();
  const aiTarget = useAiTarget();

  // Test payments run inside a preview but are real test-mode purchases, so declining
  // an upsell has to work there — only the design preview keeps the link inert.
  const isUninteractivePreview = isPreview && !isTestingPayments;

  const stripe = useStripe();
  const elements = useElements();
  const { validateCoupon } = useCheckoutCoupon();
  const { executeRecaptcha } = useRecaptcha();
  const { locale: browserLocale } = useCheckoutLocalization();
  const { declineUpsell } = useCheckoutUpsell();

  const { setFormField, setFormStateByKey } = useCheckoutFormHelpers();
  const { createPayment, createSubscription, createSubmission } = useCheckoutHelpers();
  const {
    validateMinimumChargeAmount,
    validateHasStock,
    validateVariants,
    validateTickets,
    validateOrderBumps,
  } = useCheckoutValidation();
  const { totalDueAmount, subtotalAmount, coupon } = useCartCost();

  const [readyPaymentElement, setReadyPaymentElement] = useState(null);
  const { showGuests, guestsReadyToPay } = useGuestsBeforePay();
  // A booker field error from the guest rules, shown under that field until it changes.
  const bookerFieldError = form.guestBookerError;

  const [expressCheckoutLoaded, setExpressCheckoutLoaded] = useState(false);

  const addressField = useMemo(
    () => Object.values(form.fields ?? {}).find(f => f.element === 'address'),
    [form.fields],
  );
  const { shouldHideField: addressFieldHidden } = useFieldVisibility(addressField ?? {});

  // Get labels from theme
  const labels = useMemo(() => getThemeLabels(theme), [theme]);

  // Resolve the buyer's selected price (selectedPriceId → default → first enabled).
  // null for legacy products without a prices[] array.
  const selectedPrice = useSelectedPrice();

  const prevFormFieldsRef = useRef();
  const prevFormVariantsRef = useRef();
  const prevPaymentOptionRef = useRef();
  const prevCouponRef = useRef();
  const isSubmittingRef = useRef(false);
  const paymentMethodsRef = useRef(null);

  useEffect(() => {
    async function setCoupon(couponCode) {
      try {
        await validateCoupon(couponCode);
        setShowCouponCodeField(true);
      } catch (error) {
        // no-op
      }
    }

    if (!checkout.showCouponCodeField || isUpsell) return;

    if (query.coupon) {
      setCoupon(query.coupon);
    } else if (draftCheckout?.coupon?.code) {
      setCoupon(draftCheckout.coupon.code);
    }
  }, [query, draftCheckout]);

  // Centralized draft charge update for immediate-action fields
  useEffect(() => {
    // Only update draft for actual changes, not initial render
    const fieldsChanged =
      prevFormFieldsRef.current &&
      JSON.stringify(prevFormFieldsRef.current) !== JSON.stringify(form.fields);
    const variantsChanged =
      prevFormVariantsRef.current &&
      JSON.stringify(prevFormVariantsRef.current) !== JSON.stringify(form.variants);
    const paymentOptionChanged =
      prevPaymentOptionRef.current && prevPaymentOptionRef.current?.type !== paymentOption?.type;
    const couponChanged = prevCouponRef.current !== coupon;

    // Use instant update for variants (immediate feedback for user selections)
    if (fieldsChanged || paymentOptionChanged || couponChanged || variantsChanged) {
      // Use debounced version for form fields to prevent excessive API calls while typing
      updateDraftChargeDebounced();
    }

    // Update refs
    prevFormFieldsRef.current = form.fields;
    prevFormVariantsRef.current = form.variants;
    prevPaymentOptionRef.current = paymentOption;
    prevCouponRef.current = coupon;
  }, [form.fields, form.variants, paymentOption, coupon, updateDraftChargeDebounced]);

  const showPaymentMethods = checkShouldShowPaymentMethods({
    paymentOption,
    amount: totalDueAmount,
    checkout,
    product,
    selectedPrice,
    form,
    isUpsell,
    isUpsellRetry,
    coupon,
  });

  const paymentFormType = showPaymentMethods
    ? getPaymentFormType({ page, seller, isScreenshot, isMeasurer })
    : null;
  const expectedPaymentElement = paymentFormType
    ? elements?.getElement(paymentFormType === 'payment' ? 'payment' : 'card')
    : null;
  const paymentFormLoading =
    !!paymentFormType &&
    (!expectedPaymentElement || expectedPaymentElement !== readyPaymentElement);

  // The test card needs no payment element, so it does not wait for one to load.
  const { submitIsDisabled: testCardIsDisabled } = useSubmitDisabled(false);
  const submitIsDisabled = testCardIsDisabled || paymentFormLoading;
  const [isTestCardSubmit, setIsTestCardSubmit] = useState(false);

  useEffect(() => {
    return () => {
      isSubmittingRef.current = false;
    };
  }, []);

  async function submitForm({ testCard = false } = {}) {
    if (testCard ? testCardIsDisabled : submitIsDisabled) return;
    // The test card button does not submit the form, so the browser skips its required-field check,
    // and with guests showing the form skips it too.
    if (showGuests && !reportValidityOutsideGuests(formRef.current)) return;
    if (testCard && !showGuests && formRef.current?.reportValidity() === false) return;
    if (state === checkoutStates.SUBMITTING) return;

    // Prevent double submissions with immediate ref check
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setIsTestCardSubmit(testCard);
    setFormError(null);

    try {
      // Upsells never render the address field (see `showFields` below), so it must
      // not be validated either — otherwise the one-click charge is blocked by a field
      // the customer can't see or fill in.
      if (!isUpsell && addressField && !addressFieldHidden) {
        const addressErrors = validateAddressValue(addressField, addressField.value);
        if (addressErrors.length > 0) {
          setFormError(formatAddressValidationError(addressErrors, browserLocale));
          return;
        }
      }

      // @todo separate flow for EVENT
      if (checkout.type === PAGE_TYPES.FORM) {
        cancelUpdateDraftCharge();
        setState(checkoutStates.SUBMITTING);

        try {
          // Get a fresh reCAPTCHA token right before submission
          const recaptchaToken = await executeRecaptcha('submit');

          await createSubmission({ recaptchaToken });
        } catch (error) {
          setFormError(error.message);
          setState(checkoutStates.INITIAL);
        }
      } else {
        try {
          if (!stripe && !testCard) return;

          validateVariants();
          validateTickets();
          validateOrderBumps();

          if (!guestsReadyToPay()) return;
          // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
          validateMinimumChargeAmount(payWhatYouWantPrice, product.currency, currencyLocale);
          validateHasStock();

          const paymentDetails = testCard
            ? { ok: true }
            : await checkPaymentDetails({
                stripe,
                elements,
                readyElement: readyPaymentElement,
              });

          if (!paymentDetails.ok) {
            if (paymentDetails.message) setFormError(paymentDetails.message);
            // Not focus(): Stripe always focuses the first field, even when only the CVC is missing.
            if (paymentDetails.incomplete) {
              paymentMethodsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
          }

          // Cancelled only once the payment will start: a draft update changes the window
          // history, which can leave a paid checkout hanging on the payment page.
          cancelUpdateDraftCharge();

          // Get a fresh reCAPTCHA token right before payment
          const recaptchaToken = await executeRecaptcha('checkout');

          /**
           * Once a payment has been initiated set a flag to disable future draft updates
           * For this checkout regardless of subsequent state changes or errors.
           *
           * This comment may become outdated but in @useCheckoutHelper.js
           *
           * We have a function called completePayment, in this function we set the state
           * to `COMPLETED` however in handleAfterPaymentActions we have a setTimeout
           * that reverts the `COMPLETED` state to `INITIAL` after 3 seconds.
           *
           * This was the potential cause of an issue where a draft was able to update a
           * Successful payment.
           */
          hasInitiatedPaymentRef.current = true;

          // Branch on the buyer's selected price billing type (multi-price),
          // falling back to the legacy product.type for products without a
          // prices[] array (shared gate — see lib/multiPrice).
          // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchase)
          if (isRecurringPurchase(selectedPrice, product)) {
            await createSubscription({ recaptchaToken, testCard });
          } else {
            await createPayment({ recaptchaToken, testCard });
          }
        } catch (error) {
          hasInitiatedPaymentRef.current = false;

          logException(error.message);
          setFormError(error.message);
          setState(checkoutStates.INITIAL);
          setIsTestCardSubmit(false);
        }
      }
    } finally {
      /**
       * Reset the submission flag but with a brief delay to allow page navigation to occur first.
       * If the page navigation is slower than this 500ms delay, during testing what we see is an attempt from stripe to update the processing payment_intent.
       *
       * This PaymentIntent's amount could not be updated because it has a status of succeeded. You may only update the amount of a PaymentIntent with one of the following statuses: requires_payment_method, requires_confirmation, requires_action.
       */
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 500);
    }
  }

  const hasFields = checkout.fields?.length > 0;
  const showFields = hasFields && !isUpsell;

  // Payment options (pay in full / manual / partial) are a one-time-only
  // feature: a subscription always collects a card, and the server ignores
  // paymentOption on the subscription path. Hide the picker whenever the
  // resolved purchase is recurring (selected price, legacy type fallback);
  // useCheckoutState clears a stale manual selection on the same gate.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchase)
  const showPaymentOptions =
    checkout.paymentOptions?.length > 1 && !isRecurringPurchase(selectedPrice, product);

  const hasExpressCheckout =
    checkout.paymentMethods.stripe.agpay &&
    checkout.paymentMethods.stripe.agpay?.enabled === true &&
    checkout.paymentMethods.stripe.agpay?.mode === 'express';
  const hasMerchantEnabledAppleGooglePay =
    seller.paymentGateways?.stripe?.enableAppleGooglePay === true;
  const hasCheckoutEnabledAppleGooglePay = checkout.paymentMethods.stripe.agpay?.enabled === true;
  const showExpressCheckout =
    !isUpsell &&
    !isScreenshot &&
    !isMeasurer &&
    hasExpressCheckout &&
    hasCheckoutEnabledAppleGooglePay &&
    hasMerchantEnabledAppleGooglePay &&
    showPaymentMethods;

  const showTestCardButton = shouldShowTestCardButton({
    isTestMode,
    showPaymentMethods,
    pageType: checkout.type,
    isUpsell,
    isScreenshot,
    isMeasurer,
    isUninteractivePreview,
  });

  const isBusy =
    state === checkoutStates.SUBMITTING ||
    state === checkoutStates.CREATING_PAYMENT ||
    state === checkoutStates.CREATING_SUBSCRIPTION ||
    state === checkoutStates.CREATING_SUBMISSION ||
    state === checkoutStates.COMPLETED;

  const isTestCardPaying = isBusy && isTestCardSubmit;
  const unavailableWhileTestCardPays = clsx(
    'transition-opacity duration-200',
    isTestCardPaying && 'opacity-40 pointer-events-none',
  );

  const payButtonText = getPayButtonText({
    showPriceOnButton: theme.elements?.checkout?.form?.button?.priceOnButton?.enabled !== false,
    page: checkout,
    product: product,
    selectedPrice: selectedPrice,
    locale: currencyLocale,
    subtotalAmount: subtotalAmount,
    totalDueAmount: totalDueAmount,
    formVariants: form.variants,
    labels: labels,
  });

  const showPriceBreakdown =
    (checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
    theme?.elements?.checkout?.form?.priceBreakdown?.enabled !== false;

  const guestErrorResolved =
    formError === GUEST_ERROR_MESSAGES.everyGuest &&
    currentGuestRows(checkout, form).every(row => row.done);

  return (
    <StyledElement
      elementPath="checkout.form"
      as="form"
      onSubmit={e => {
        e.preventDefault();

        submitForm();
      }}
      onFocus={() => {
        hasFocusedCheckoutRef.current = true;
      }}
      method="post"
      noValidate={showGuests}
      className={clsx(
        'cp_form m-auto space-y-4 w-full pb-4',
        'text-[var(--cp_checkout-form-color)]',
      )}
      ref={formRef}
    >
      {/* form-top is an alternate position for the single-price PWYW input
          (variables.elements.checkout.pwyw.position); ProductPwywField
          self-gates so this is a no-op unless the theme picks this site. */}
      <ProductPwywField position="form-top" />

      {showExpressCheckout && (
        <div
          className={clsx('cp_express-checkout-wrapper', unavailableWhileTestCardPays)}
          aria-disabled={isTestCardPaying || undefined}
          style={{ display: expressCheckoutLoaded ? 'block' : 'none' }}
        >
          <StyledElement
            elementPath="checkout.form.expressCheckout.stripeExpressCheckoutElement"
            as="div"
            className={clsx(
              'cp_express-checkout cp_stripe-express-checkout-element-wrapper relative flex flex-col space-y-2',
              'text-[var(--cp_checkout-form-expressCheckout-stripeExpressCheckoutElement-color)]',
            )}
          >
            <ExpressCheckoutElement mode="express" onReady={() => setExpressCheckoutLoaded(true)} />
          </StyledElement>

          {theme.elements?.checkout?.form?.orPayWithCard?.enabled !== false && (
            <StyledElement
              elementPath="checkout.form.orPayWithCard"
              as="div"
              className="cp_pay-with-card-wrapper flex items-center py-2 gap-2"
            >
              <hr
                className={clsx(
                  'cp_pay-with-card-divider h-px flex-1 border-none',
                  'bg-[var(--cp_checkout-form-orPayWithCard-border-color)]',
                )}
              />
              <p
                className={clsx(
                  'cp_pay-with-card-text whitespace-nowrap text-center',
                  '[font-size:var(--cp_font-body-size-sm)]',
                  '[line-height:var(--cp_font-body-line-height-sm)]',
                  'text-[var(--cp_checkout-form-orPayWithCard-color)]',
                )}
              >
                {labels.orPayWithCardText}
              </p>
              <hr
                className={clsx(
                  'cp_pay-with-card-divider h-px flex-1 border-none',
                  'bg-[var(--cp_checkout-form-orPayWithCard-border-color)]',
                )}
              />
            </StyledElement>
          )}
        </div>
      )}

      {showFields && (
        <StyledElement
          elementPath="checkout.form.fields"
          as="div"
          {...aiTarget('fields')}
          className={clsx(
            'cp_form-fields',
            'bg-[var(--cp_checkout-form-fields-background-color)]',
            'text-[var(--cp_checkout-form-fields-color)]',
          )}
        >
          <FormLayoutFields
            renderField={field => (
              <>
                <FormField
                  field={field}
                  onChange={(value, meta) => {
                    if (bookerFieldError?.fieldId === field._id) {
                      setFormStateByKey('guestBookerError', null);
                    }
                    setFormField(field._id, value, meta);
                  }}
                  onBlur={updateDraftChargeDebounced}
                />
                {bookerFieldError?.fieldId === field._id && (
                  <ErrorMessage className="cp_error -mt-2 mb-4">
                    {bookerFieldError.message}
                  </ErrorMessage>
                )}
              </>
            )}
          />
        </StyledElement>
      )}

      {showGuests && <GuestRows />}

      {/* form-bottom is an alternate position for the single-price PWYW input
          (variables.elements.checkout.pwyw.position); ProductPwywField
          self-gates so this is a no-op unless the theme picks this site. */}
      <ProductPwywField position="form-bottom" />

      {/* {(checkout.billingAddressCollection ||
        checkout.shippingAddressCollection) && (
        <div className="cp_address-wrapper ">
          <AddressField />
        </div>
      )} */}

      {/* A page that places the discount code in its form layout shows it there, when the fields show. */}
      {(!showFields || !layoutHasDiscountCode(checkout.formLayout)) && <DiscountCodeBox />}

      {showPaymentOptions && <PaymentOptions />}

      {showPaymentMethods && (
        <div
          ref={paymentMethodsRef}
          {...aiTarget('paymentMethods')}
          className={clsx('cp_payment-method-wrapper', unavailableWhileTestCardPays)}
          aria-disabled={isTestCardPaying || undefined}
        >
          <PaymentMethods onReady={setReadyPaymentElement} />
        </div>
      )}

      {!!formError && !guestErrorResolved && (
        <div className="cp_error-wrapper">
          <APIErrorMessage className="cp_error">{formError}</APIErrorMessage>
        </div>
      )}

      <CoverProcessingFees />

      {showPriceBreakdown && <PriceBreakdown />}

      {showTestCardButton && (
        <TestCardPayButton
          text={payButtonText}
          disabled={testCardIsDisabled || isBusy}
          isSubmitting={isBusy && isTestCardSubmit}
          spinnerColor={checkout.buttonTextColor}
          onPay={() => submitForm({ testCard: true })}
        />
      )}
      <div
        {...aiTarget('payButton')}
        className={clsx('cp_pay-button-wrapper', {
          'pt-2': !showPriceBreakdown,
        })}
      >
        {/* Absorb Enter-key submissions from input fields on checkout/event pages
            so an accidental Enter doesn't confirm an order. Form pages keep the
            standard browser behavior where Enter submits. */}
        {checkout.type !== PAGE_TYPES.FORM && (
          <button type="submit" disabled className="hidden" aria-hidden="true" />
        )}

        <StyledElement
          elementPath="checkout.form.button"
          as={Button}
          className={clsx(
            'cp_button cp_pay-button whitespace-nowrap leading-snug',
            unavailableWhileTestCardPays,
          )}
          size="full"
          type="submit"
          appearance="submit"
          disabled={submitIsDisabled || isBusy}
        >
          {isBusy && !isTestCardSubmit ? (
            <span className="inline-flex items-center justify-center h-[1lh]">
              <Spinner
                className="cp_spinner"
                width="22px"
                height="22px"
                appearance="custom"
                style={{
                  color: checkout.buttonTextColor,
                }}
              />
            </span>
          ) : (
            payButtonText
          )}
        </StyledElement>

        {isUpsell && (
          <div className="cp_upsell-decline-button-wrapper text-center mt-3">
            <button
              type="button"
              onClick={isUninteractivePreview ? undefined : declineUpsell}
              className={clsx(
                'cp_upsell-decline-button whitespace-nowrap leading-snug text-[var(--cp_text-primary)] underline',
                isUninteractivePreview && '!cursor-not-allowed',
              )}
            >
              {labels.upsellNoThanksText}
            </button>
          </div>
        )}
      </div>

      {(checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
        theme.elements?.checkout?.form?.securePayments?.enabled !== false &&
        !isScreenshot &&
        !isUpsell && (
          <StyledElement
            elementPath="checkout.form.securePayments"
            as="div"
            className={clsx(
              'cp_secure-payments flex justify-center items-center gap-x-1 mt-4',
              'bg-[var(--cp_checkout-form-securePayments-background-color)]',
              'text-[var(--cp_checkout-form-securePayments-color)]',
              '[font-family:var(--cp_checkout-form-securePayments-font-family)]',
              '[font-style:var(--cp_checkout-form-securePayments-font-style)]',
              '[font-size:var(--cp_checkout-form-securePayments-font-size)]',
              '[line-height:var(--cp_checkout-form-securePayments-line-height)]',
              '[font-weight:var(--cp_checkout-form-securePayments-font-weight)]',
            )}
          >
            <LockClosedIcon className="cp_secure-payments-icon w-4 h-4" />
            <span className="cp_secure-payments-text">{labels.securePaymentsText}</span>
          </StyledElement>
        )}
    </StyledElement>
  );
};

export default CheckoutForm;
